import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
  useCallback,
} from "react";
import { Session, User, RealtimeChannel } from "@supabase/supabase-js";
import { useToast } from "@/hooks/toast/index";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

// Define StoreApprovalStatus type
export type StoreApprovalStatus = 'pending' | 'approved' | 'rejected' | null;

export interface Profile { 
  id: string;
  username?: string;
  full_name?: string;
  email?: string;
  phone_number?: string;
  location?: string;
  birth_place?: string;
  profile_image?: string;
  role?: "admin" | "user";
  is_blocked?: boolean;
  created_at: string; 
  updated_at?: string; 
  is_store?: boolean;
  store_name?: string;
  store_banner_url?: string;
  store_description?: string;
  store_approval_status?: StoreApprovalStatus;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    userData: { 
      username: string; 
      full_name: string; 
      location?: string;
      terms_accepted?: boolean;
      privacy_accepted?: boolean;
      ip_address?: string;
      is_store?: boolean;
    }
  ) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  isLoading: boolean;
  isAuthenticated: boolean;
  refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { toast } = useToast(); // This toast is now stable
  const navigate = useNavigate();
  const isMountedRef = useRef(true);
  const isToastShownRef = useRef(new Set<string>());
  const isFetchingProfileRef = useRef(false);
  const profileChannelRef = useRef<RealtimeChannel | null>(null);
  const profileRef = useRef<Profile | null>(null);

  const fetchProfile = useCallback(async (userId: string, retryCount = 0) => {
    if (!isMountedRef.current) return;

    if (isFetchingProfileRef.current && retryCount === 0) {
       console.log("AUTH_CONTEXT: Profile fetch already in progress for user (checked inside fetchProfile):", userId);
       if (profileRef.current?.id === userId && retryCount === 0) {
          console.log("AUTH_CONTEXT: Profile for user already seems to be loaded, not re-fetching unless forced.");
       }
    }
    
    isFetchingProfileRef.current = true;
    console.log(`AUTH_CONTEXT: Fetching profile for user ${userId}, attempt ${retryCount + 1}`);

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) {
        if (retryCount < 3 && isMountedRef.current) {
          console.warn(`AUTH_CONTEXT: Error fetching profile (attempt ${retryCount + 1}), retrying...`, error.message);
          setTimeout(() => {
            if (isMountedRef.current) {
              fetchProfile(userId, retryCount + 1);
            }
          }, 1000 * (retryCount + 1) * (retryCount + 1));
        } else if (isMountedRef.current) {
          console.error("AUTH_CONTEXT: Error fetching profile after retries:", error);
          if (!isToastShownRef.current.has('FETCH_PROFILE_ERROR')) {
            toast({
              title: "Problem me profilin",
              description: "Nuk mundëm të ngarkonim të dhënat e profilit tuaj. Ju lutemi provoni të rifreskoni faqen.",
              variant: "destructive",
            });
            isToastShownRef.current.add('FETCH_PROFILE_ERROR');
          }
        }
      } else if (data && isMountedRef.current) {
        console.log("AUTH_CONTEXT: Profile data fetched successfully:", data);
        const typedProfile: Profile = {
          ...data,
          id: data.id,
          created_at: data.created_at,
          role: (data.role === "admin" ? "admin" : "user") as "admin" | "user",
          is_store: data.is_store ?? false,
          store_name: data.store_name ?? undefined,
          store_banner_url: data.store_banner_url ?? undefined,
          store_description: data.store_description ?? undefined,
          store_approval_status: data.store_approval_status as StoreApprovalStatus ?? null,
          updated_at: data.updated_at ?? undefined,
        };
        setProfile(typedProfile);
        profileRef.current = typedProfile;
        isToastShownRef.current.delete('FETCH_PROFILE_ERROR'); 
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error("AUTH_CONTEXT: Unexpected error in fetchProfile:", error);
        if (!isToastShownRef.current.has('FETCH_PROFILE_UNEXPECTED_ERROR')) {
          toast({
            title: "Gabim i papritur",
            description: "Ndodhi një gabim gjatë ngarkimit të profilit.",
            variant: "destructive",
          });
          isToastShownRef.current.add('FETCH_PROFILE_UNEXPECTED_ERROR');
        }
      }
    } finally {
       if (isMountedRef.current) {
         isFetchingProfileRef.current = false;
       }
    }
  }, [toast]); // toast is now stable, so fetchProfile is more stable

  const refreshUserProfile = useCallback(async () => {
    if (user && isMountedRef.current) {
      console.log("AUTH_CONTEXT: Explicitly refreshing user profile for:", user.id);
      isFetchingProfileRef.current = false; 
      await fetchProfile(user.id);
    }
  }, [user, fetchProfile]);

  useEffect(() => {
    console.log("AUTH_CONTEXT: Main useEffect mounting/running.");
    isMountedRef.current = true;
    isToastShownRef.current = new Set();

    const {
      data: { subscription: authSubscription },
    } = supabase.auth.onAuthStateChange((event, currentSession) => {
      if (!isMountedRef.current) {
        console.log("AUTH_CONTEXT: onAuthStateChange - isMountedRef is false, returning.");
        return;
      }
      console.log("AUTH_CONTEXT: onAuthStateChange event:", event, "session:", currentSession);

      setSession(currentSession);
      const newAuthUser = currentSession?.user ?? null;
      setUser(newAuthUser);
      
      const currentProfileForComparison = profileRef.current; 

      if (newAuthUser && event !== "SIGNED_OUT") {
        console.log("AUTH_CONTEXT: User signed in/updated. NewAuthUser ID:", newAuthUser.id, "CurrentProfile ID:", currentProfileForComparison?.id);
        if (newAuthUser.id !== currentProfileForComparison?.id || !currentProfileForComparison) {
          console.log("AUTH_CONTEXT: Fetching profile due to new user or no current profile.");
          if (newAuthUser.id !== currentProfileForComparison?.id) {
            isFetchingProfileRef.current = false; 
          }
          setTimeout(() => {
            if (isMountedRef.current) {
              fetchProfile(newAuthUser.id);
            }
          }, 0);
        } else {
          console.log("AUTH_CONTEXT: User ID matches current profile ID, not fetching profile on this event.");
        }
      } else if (event === "SIGNED_OUT") {
        if (isMountedRef.current) {
          console.log("AUTH_CONTEXT: User signed out. Clearing profile.");
          setProfile(null);
          profileRef.current = null;
        }
      } else if (event === "USER_UPDATED") {
         if (isMountedRef.current) {
            if (currentSession?.user) {
              console.log("AUTH_CONTEXT: User updated. Session user email:", currentSession.user.email, "CurrentProfile email:", currentProfileForComparison?.email);
              if (currentSession.user.email !== currentProfileForComparison?.email || currentSession.user.id !== currentProfileForComparison?.id) {
                console.log("AUTH_CONTEXT: Fetching profile due to user data update (email/id mismatch).");
                isFetchingProfileRef.current = false;
                fetchProfile(currentSession.user.id);
              } else {
                 console.log("AUTH_CONTEXT: User update event, but core identifiers match, not refetching profile based on this check.");
              }
            }
            if (!isToastShownRef.current.has('USER_UPDATED')) {
              isToastShownRef.current.add('USER_UPDATED');
              setTimeout(() => {
                if (isMountedRef.current) {
                  toast({
                    title: "Llogaria u përditësua",
                    description: "Të dhënat e llogarisë tuaj u përditësuan me sukses",
                  });
                }
              }, 100);
            }
         }
      } else if (event === "PASSWORD_RECOVERY" && isMountedRef.current) {
        if (!isToastShownRef.current.has('PASSWORD_RECOVERY')) {
          isToastShownRef.current.add('PASSWORD_RECOVERY');
          setTimeout(() => {
            if (isMountedRef.current) {
              toast({
                title: "Rivendosje e Fjalëkalimit",
                description: "Ju lutemi ndiqni instruksionet për të rivendosur fjalëkalimin tuaj",
              });
            }
          }, 100);
        }
      }
    });

    const initializeAuth = async () => {
      console.log("AUTH_CONTEXT: Initializing auth...");
      try {
        const {
          data: { session: initialSession },
        } = await supabase.auth.getSession();

        if (!isMountedRef.current) {
          console.log("AUTH_CONTEXT: initializeAuth - isMountedRef is false, returning.");
          return;
        }
        console.log("AUTH_CONTEXT: Initial session fetched:", initialSession ? initialSession.user?.id : 'null');

        setSession(initialSession);
        setUser(initialSession?.user ?? null);

        const currentProfileForComparison = profileRef.current;
        if (initialSession?.user && isMountedRef.current) {
          console.log("AUTH_CONTEXT: Initial session user ID:", initialSession.user.id, "CurrentProfile ID:", currentProfileForComparison?.id);
          if (initialSession.user.id !== currentProfileForComparison?.id || !currentProfileForComparison) {
            console.log("AUTH_CONTEXT: Fetching profile during initialization.");
            isFetchingProfileRef.current = false;
            await fetchProfile(initialSession.user.id);
          } else {
            console.log("AUTH_CONTEXT: Profile for initial user seems to be already loaded/aligned.");
          }
        } else {
          console.log("AUTH_CONTEXT: No initial user session or already unmounted.");
        }
      } catch (error) {
        if (isMountedRef.current) {
          toast({ // This toast call will use the stable toast instance from the AuthProvider scope
            title: "Gabim Autentifikimi",
            description:
              "Dështoi në inicializimin e autentifikimit. Ju lutemi rifreskoni faqen.",
            variant: "destructive",
          });
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
          console.log("AUTH_CONTEXT: Auth initialization finished, isLoading set to false.");
        }
      }
    };

    initializeAuth();

    return () => {
      console.log("AUTH_CONTEXT: Main useEffect cleanup running.");
      isMountedRef.current = false;
      authSubscription.unsubscribe();
      if (profileChannelRef.current) {
        supabase.removeChannel(profileChannelRef.current);
        profileChannelRef.current = null;
        console.log("AUTH_CONTEXT: Removed profile Realtime channel on unmount/user change (main effect cleanup).");
      }
    };
  }, []); // Dependency array changed from [toast] to []

  useEffect(() => {
    if (user?.id && isMountedRef.current) {
      if (profileChannelRef.current && profileChannelRef.current.topic !== `profile_changes_${user.id}`) {
        supabase.removeChannel(profileChannelRef.current);
        profileChannelRef.current = null;
        console.log("AUTH_CONTEXT: User ID changed or channel mismatch, removed existing profile Realtime channel.");
      }

      if (!profileChannelRef.current) {
        console.log(`AUTH_CONTEXT: Setting up Realtime channel for profile: ${user.id}`);
        const channel = supabase
          .channel(`profile_changes_${user.id}`)
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'profiles',
              filter: `id=eq.${user.id}`,
            },
            (payload) => {
              if (isMountedRef.current) {
                console.log('AUTH_CONTEXT: Profile changed via Realtime, new data:', payload.new);
                const newProfileData = payload.new as Profile;
                const currentProfileForComparison = profileRef.current; 
                
                const hasSignificantChange = newProfileData && currentProfileForComparison && (
                  newProfileData.store_approval_status !== currentProfileForComparison.store_approval_status ||
                  newProfileData.is_store !== currentProfileForComparison.is_store ||
                  newProfileData.username !== currentProfileForComparison.username ||
                  newProfileData.full_name !== currentProfileForComparison.full_name ||
                  newProfileData.profile_image !== currentProfileForComparison.profile_image ||
                  newProfileData.store_name !== currentProfileForComparison.store_name ||
                  newProfileData.store_banner_url !== currentProfileForComparison.store_banner_url ||
                  newProfileData.store_description !== currentProfileForComparison.store_description ||
                  newProfileData.is_blocked !== currentProfileForComparison.is_blocked ||
                  newProfileData.role !== currentProfileForComparison.role
                );

                if (hasSignificantChange || !currentProfileForComparison) { 
                   console.log("AUTH_CONTEXT: Significant profile change detected via Realtime, updating profile data.");
                   const updatedProfileState: Profile = {
                     ...(currentProfileForComparison || {} as Profile), 
                     ...newProfileData, 
                     id: newProfileData.id || currentProfileForComparison?.id || user.id, 
                     created_at: newProfileData.created_at || currentProfileForComparison?.created_at || '', 
                   };
                   setProfile(updatedProfileState);
                   profileRef.current = updatedProfileState; 
                   toast({
                      title: "Profili u përditësua",
                      description: "Informacioni i profilit tuaj është përditësuar në kohë reale.",
                   });
                } else {
                  console.log("AUTH_CONTEXT: Profile change detected via Realtime, but no significant fields seem to have changed. Data might be identical or only non-critical fields changed.");
                }
              }
            }
          )
          .subscribe((status, err) => {
            if (status === 'SUBSCRIBED') {
              console.log(`AUTH_CONTEXT: Successfully subscribed to profile changes for user ${user.id}`);
            }
            if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
              console.error(`AUTH_CONTEXT: Realtime subscription error for profile ${user.id}:`, status, err);
            }
          });
        profileChannelRef.current = channel;
      }
    } else {
      if (profileChannelRef.current) {
        supabase.removeChannel(profileChannelRef.current);
        profileChannelRef.current = null;
        console.log("AUTH_CONTEXT: User logged out or component unmounted, removed profile Realtime channel.");
      }
    }
  }, [user, fetchProfile, toast]);


  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (isMountedRef.current) {
        if(data.user) {
          isFetchingProfileRef.current = false;
          await fetchProfile(data.user.id);
        }
        setTimeout(() => {
          if (isMountedRef.current) {
            toast({
              title: "Hyrje e suksesshme",
              description: "Mirë se u kthyet!",
            });
          }
        }, 100);
      }
    } catch (error: any) {
      if (isMountedRef.current) {
        toast({
          title: "Hyrja dështoi",
          description:
            error.message ||
            "Ju lutemi kontrolloni kredencialet tuaja dhe provoni përsëri",
          variant: "destructive",
        });
      }
      throw error;
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const register = async (
    email: string,
    password: string,
    userData: { 
      username: string; 
      full_name: string;
      location?: string;
      terms_accepted?: boolean;
      privacy_accepted?: boolean;
      ip_address?: string;
      is_store?: boolean;
    }
  ) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            ...userData,
            store_approval_status: userData.is_store ? 'pending' : null 
          },
          emailRedirectTo: `${window.location.origin}/auth/account-activated`,
        },
      });

      if (error) {
        throw error;
      }

      const params = new URLSearchParams();
      params.append('email', email);
      params.append('name', userData.full_name || userData.username);

      if (isMountedRef.current) {
        navigate(`/auth/registration-success?${params.toString()}`, { replace: true });
      }

    } catch (error: any) {
      if (isMountedRef.current) {
        toast({
          title: "Regjistrimi dështoi",
          description: error.message || "Ju lutemi provoni përsëri më vonë",
          variant: "destructive",
        });
      }
      throw error;
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      if (isMountedRef.current) {
        setUser(null);
        setProfile(null);
        profileRef.current = null; 
        setSession(null);
        isFetchingProfileRef.current = false; 
        if (profileChannelRef.current) { 
          supabase.removeChannel(profileChannelRef.current);
          profileChannelRef.current = null;
          console.log("AUTH_CONTEXT: Logged out, removed profile Realtime channel.");
        }

        setTimeout(() => {
          if (isMountedRef.current) {
            toast({
              title: "Dalje e suksesshme",
              description: "Ju keni dalë me sukses nga llogaria",
            });
          }
        }, 100);
      }
    } catch (error: any) {
      if (isMountedRef.current) {
        toast({
          title: "Dalja dështoi",
          description: error.message || "Ndodhi një gabim gjatë daljes",
          variant: "destructive",
        });
      }
    }
  };

  const resetPassword = async (email: string) => {
    if (!email || !email.trim()) {
      if (isMountedRef.current) {
        toast({
          title: "Gabim në rivendosjen e fjalëkalimit",
          description: "Ju lutem vendosni një email të vlefshëm",
          variant: "destructive",
        });
      }
      return Promise.reject(
        new Error("Ju lutem vendosni një email të vlefshëm")
      );
    }

    setIsLoading(true);
    try {
      const currentOrigin = window.location.origin;
      const redirectTo = `${currentOrigin}/reset-password`;

      console.log(`Sending password reset email with redirectTo: ${redirectTo}`);

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectTo,
      });

      if (error) {
        throw error;
      }

      return Promise.resolve();
    } catch (error: any) {
      if (isMountedRef.current) {
        toast({
          title: "Rivendosja e fjalëkalimit dështoi",
          description:
            error.message ||
            "Ndodhi një gabim gjatë dërgimit të email-it për rivendosjen e fjalëkalimit.",
          variant: "destructive",
        });
      }
      throw error; 
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        session,
        login,
        register,
        logout,
        resetPassword,
        isLoading,
        isAuthenticated: !!user,
        refreshUserProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
